/**
 * Returns the correct component style overrides object based on the provided mode.
 * @param {object} config - The configuration object.
 * @param {'preLogin' | 'postLogin'} config.mode - The primary theme mode.
 * @param {'light' | 'dark'} config.colorMode - The color mode for the preLogin theme's dynamic styles.
 * @param {object} config.themeOption - The blueprint for the postLogin theme.
 * @returns A complete MUI components object.
 */
const getComponentOverrides = ({ themeOption, mode, colorMode }) => {
    switch (mode) {
        case 'preLogin':
            // This is the complete set of overrides from the original pre-login/index.js file.
            return {
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontWeight: 400,
                            borderRadius: 5,
                            paddingTop: 10,
                            paddingBottom: 10,
                        },
                        containedSecondary: colorMode === 'light' ? { color: 'white' } : {},
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            borderRadius: 5,
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: 5,
                        },
                        input: {
                            borderRadius: 5,
                        },
                    },
                },
                MuiCard: {
                    styleOverrides: {
                        root: {
                            borderRadius: 8,
                        },
                    },
                },
            };

        case 'postLogin':
        default:
            // This is the complete set of overrides from the original post-login/compStyleOverride.js file you provided.
            return {
                MuiRating: {
                    styleOverrides: {
                        root: {
                            color: themeOption.colors.primaryMain,
                        },
                    },
                },
                MuiToggleButton: {
                    styleOverrides: {
                        root: {
                            fontSize: '16px',
                            '&.Mui-selected': {
                                backgroundColor: themeOption.colors.primaryMain,
                                color: themeOption.colors.paper,
                            },
                            '&:hover': {
                                backgroundColor: themeOption.menuSelectedBack,
                                color: themeOption.colors.primaryMain,
                            },
                        },
                    },
                },
                MuiTimelineItem: {
                    styleOverrides: {
                        root: {
                            '&:before': {
                                flex: 0,
                                padding: 0,
                            },
                        },
                        missingOppositeContent: {
                            flex: 0,
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        shrink: {
                            fontSize: '1.2em',
                            color: `${themeOption.colors.primaryMain}`,
                        },
                        asterisk: {
                            fontSize: '18px',
                            color: `${themeOption.colors.errorDark}`,
                        },
                        outlined: {
                            '&$shrink': {
                                transform: 'translate(14px, -6px) scale(0.9)',
                            },
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            fontWeight: 500,
                            textTransform: 'capitalize',
                            borderRadius: '4px',
                        },
                    },
                },
                MuiPaper: {
                    defaultProps: {
                        elevation: 0,
                    },
                    styleOverrides: {
                        root: {
                            backgroundImage: 'none',
                        },
                        rounded: {
                            borderRadius: `${themeOption.customization.borderRadius}px`,
                        },
                    },
                },
                MuiCardHeader: {
                    styleOverrides: {
                        root: {
                            color: themeOption.colors.textDark,
                            padding: '24px',
                        },
                        title: {
                            fontSize: '1.125rem',
                        },
                    },
                },
                MuiCardContent: {
                    styleOverrides: {
                        root: {
                            padding: '24px',
                        },
                    },
                },
                MuiCardActions: {
                    styleOverrides: {
                        root: {
                            padding: '24px',
                        },
                    },
                },
                MuiListItemButton: {
                    styleOverrides: {
                        root: {
                            color: themeOption.darkTextPrimary,
                            paddingTop: '10px',
                            paddingBottom: '10px',
                            '&.Mui-selected': {
                                color: themeOption.menuSelected,
                                backgroundColor: themeOption.menuSelectedBack,
                                '&:hover': {
                                    backgroundColor: themeOption.menuSelectedBack,
                                },
                                '& .MuiListItemIcon-root': {
                                    color: themeOption.menuSelected,
                                },
                            },
                            '&:hover': {
                                backgroundColor: themeOption.menuSelectedBack,
                                color: themeOption.menuSelected,
                                '& .MuiListItemIcon-root': {
                                    color: themeOption.menuSelected,
                                },
                            },
                        },
                    },
                },
                MuiListItemIcon: {
                    styleOverrides: {
                        root: {
                            color: themeOption.darkTextPrimary,
                            minWidth: '36px',
                        },
                    },
                },
                MuiListItemText: {
                    styleOverrides: {
                        primary: {
                            color: themeOption.textDark,
                        },
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        input: {
                            color: themeOption.textDark,
                            '&::placeholder': {
                                color: themeOption.darkTextSecondary,
                                fontSize: '0.875rem',
                            },
                        },
                    },
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            background: themeOption.colors.grey50,
                            borderRadius: `${themeOption.customization.borderRadius}px`,
                            '& .MuiOutlinedInput-notchedOutline': {
                                // NOTE: Changed grey400 to grey300 as grey400 was not defined in the original colors file.
                                borderColor: themeOption.colors.grey300,
                            },
                            '&:hover $notchedOutline': {
                                borderColor: themeOption.colors.primaryLight,
                            },
                            '&.MuiInputBase-multiline': {
                                padding: 1,
                            },
                        },
                        input: {
                            fontWeight: 500,
                            background: themeOption.colors.grey50,
                            padding: '15.5px 14px',
                            borderRadius: `${themeOption.customization.borderRadius}px`,
                            '&.MuiInputBase-inputSizeSmall': {
                                padding: '10px 14px',
                                '&.MuiInputBase-inputAdornedStart': {
                                    paddingLeft: 0,
                                },
                            },
                        },
                        inputAdornedStart: {
                            paddingLeft: 4,
                        },
                        notchedOutline: {
                            borderRadius: `${themeOption.customization.borderRadius}px`,
                            '& > legend': {
                                fontSize: ' 0.9em',
                            },
                        },
                    },
                },
                MuiSlider: {
                    styleOverrides: {
                        root: {
                            '&.Mui-disabled': {
                                color: themeOption.colors.grey300,
                            },
                        },
                        mark: {
                            backgroundColor: themeOption.paper,
                            width: '4px',
                        },
                        valueLabel: {
                            color: themeOption.colors.primaryLight,
                        },
                    },
                },
                MuiDivider: {
                    styleOverrides: {
                        root: {
                            borderColor: themeOption.divider,
                            opacity: 1,
                        },
                    },
                },
                MuiAvatar: {
                    styleOverrides: {
                        root: {
                            color: themeOption.colors.primaryDark,
                            background: themeOption.colors.primary200,
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        root: {
                            '&.MuiChip-deletable .MuiChip-deleteIcon': {
                                color: 'inherit',
                            },
                        },
                    },
                },
                MuiTooltip: {
                    styleOverrides: {
                        tooltip: {
                            color: themeOption.paper,
                            background: themeOption.colors.grey700,
                        },
                    },
                },
                MuiTableSortLabel: {
                    styleOverrides: {
                        root: {
                            '&.Mui-active': {
                                color: themeOption.colors.paper,
                            },
                            '&.Mui-active .MuiTableSortLabel-icon': {
                                color: themeOption.colors.paper,
                            },
                        },
                    },
                },
            };
    }
};

export default getComponentOverrides;